import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';
import type {
  OrganizationModule,
  EnableModuleDto,
  UpdateModuleSettingsDto,
  EnableModuleResponse,
  DisableModuleDto,
  DisableModuleResponse
} from '../../types/subscriptions';

const QUERY_KEYS = {
  orgModules: (orgId: string) => ['organizations', orgId, 'modules'] as const,
  allModules: ['modules'] as const,
};

export const useOrgModules = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.orgModules(orgId),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OrganizationModule[]>(
        `/features/modules/active`,
        { params: { organizationId: orgId } }
      );
      return data;
    },
    enabled: !!orgId,
  });
};

export const useEnableModule = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ moduleKey, ...dto }: EnableModuleDto & { moduleKey: string }) => {
      const { data } = await axiosInstance.post<EnableModuleResponse>(
        `/features/modules/${moduleKey}/enable`,
        dto,
        { params: { organizationId: orgId } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgModules(orgId) });
    },
  });
};

export const useDisableModule = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ moduleKey, ...dto }: DisableModuleDto & { moduleKey: string }) => {
      const { data } = await axiosInstance.post<DisableModuleResponse>(
        `/features/modules/${moduleKey}/disable`,
        dto,
        { params: { organizationId: orgId } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgModules(orgId) });
    },
  });
};

export const useStartTrial = (orgId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (moduleKey: string) => {
      const { data } = await axiosInstance.post<OrganizationModule>(
        `/features/modules/${moduleKey}/trial`,
        {},
        { params: { organizationId: orgId } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.orgModules(orgId) });
    },
  });
};

export const useModuleSettings = (orgId: string, moduleKey: string) => {
  return useQuery({
    queryKey: ['organizations', orgId, 'modules', moduleKey, 'settings'] as const,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Record<string, any>>(
        `/features/modules/${moduleKey}/settings`,
        { params: { organizationId: orgId } }
      );
      return data;
    },
    enabled: !!orgId && !!moduleKey,
  });
};

export const useUpdateModuleSettings = (orgId: string, moduleKey: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdateModuleSettingsDto) => {
      const { data } = await axiosInstance.patch<Record<string, any>>(
        `/features/modules/${moduleKey}/settings`,
        dto,
        { params: { organizationId: orgId } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organizations', orgId, 'modules', moduleKey, 'settings']
      });
    },
  });
};
